import { MenuItem, SxProps, Divider, Box } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';
import {
  ShareNetwork as ShareNetworkIcon,
  Integrations as IntegrationsIcon,
  SignOut as SignOutIcon,
  User as UserIcon,
  Chat as ChatIcon,
  Calendar as CalendarIcon,
  Notification as NotificationIcon,
  ChevronRight,
  HelpCenter,
  EnrichLeads,
} from '@sweep-io/sweep-design/dist/icons';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import useLogout from '../../../auth/useLogout';
import { UserInfo } from '../../../types/UserInfoTypes';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { openIntercom } from '../customerSupportHelper';
import { ReactNode } from 'react';
import WithNotificationDot from '../../common/WithNotificationDot';
import { sharedHoverMenuItemStyles } from '../../../appTheme/appTheme';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../services/events';
import useBookDemoClickHandler from '../../../hooks/useBookDemoClickHandler';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { UserSettingsTab } from '../user-settings/types';
import { appRoutes } from '../../../constants/appRoutes';
import { useNavigate } from 'react-router';

const MenuItemsUser = ({
  openMyProfileDialog,
  openOrgDialog,
  closeMenu,
  openIntegrationsDialog,
  openNotificationsPopover,
  hasUnreadNotifications,
  isNotificationsPopoverOpen,
}: {
  openMyProfileDialog: () => void;
  openOrgDialog: () => void;
  closeMenu: () => void;
  openIntegrationsDialog: () => void;
  openNotificationsPopover: () => void;
  hasUnreadNotifications: boolean;
  isNotificationsPopoverOpen: boolean;
}) => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const userInfo = useSelector(selectUserInfoData);
  const sendBiEvent = useSendBiEvent();
  const onBookDemoClick = useBookDemoClickHandler();
  const { multiAccounts } = useFeatureToggle();

  return (
    <>
      {multiAccounts ? (
        <>
          <Box sx={{ padding: '4px 0 4px 12px', height: '32px' }}>
            <Typography variant="caption" color={colors.grey[700]}>
              {userInfo?.email}
            </Typography>
          </Box>
          <UserMenuItem
            text="My profile"
            callback={() => {
              closeMenu();
              navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.PROFILE}`);
            }}
            startIcon={<UserIcon size="medium" />}
          />
          <Divider />
          <Box sx={{ padding: '4px 0 4px 12px', height: '32px' }}>
            <Typography variant="caption" color={colors.grey[700]}>
              {userInfo?.account?.name || 'Account'}
            </Typography>
          </Box>
          <UserMenuItem
            text="Users"
            callback={() => {
              closeMenu();
              navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.USERS}`);
            }}
            startIcon={<ShareNetworkIcon size="medium" />}
          />
          <UserMenuItem
            text="Integrations"
            callback={() => {
              closeMenu();
              navigate(appRoutes.settings.route + `?tab=${UserSettingsTab.INTEGRATIONS}`);
            }}
            startIcon={<IntegrationsIcon size="medium" />}
          />
          <UserMenuItem
            text="Switch account"
            callback={() => {
              closeMenu();
            }}
            startIcon={<EnrichLeads size="medium" />}
          />
        </>
      ) : (
        <>
          {userInfo?.email && (
            <UserMenuItem
              text={userInfo.email}
              user={userInfo}
              MenuItemPropsSx={{ '&:hover': { background: 'transparent', cursor: 'default' } }}
            />
          )}
          <Divider />
          <UserMenuItem
            text="My profile"
            callback={() => {
              closeMenu();
              openMyProfileDialog();
            }}
            startIcon={<UserIcon size="medium" />}
          />
          <UserMenuItem
            text="Workspace"
            callback={() => {
              closeMenu();
              openOrgDialog();
            }}
            startIcon={<ShareNetworkIcon size="medium" />}
          />
        </>
      )}

      <Divider />

      <UserMenuItem
        text="Chat with us"
        callback={() => {
          closeMenu();
          openIntercom();
        }}
        startIcon={<ChatIcon size="medium" />}
      />

      <UserMenuItem
        text="Schedule a call"
        callback={() => {
          onBookDemoClick();
          closeMenu();
        }}
        startIcon={<CalendarIcon size="medium" />}
      />

      <UserMenuItem
        text="Help"
        callback={() => {
          sendBiEvent({ name: UI_EVENTS.openHelpCenter });
          window.open('https://help.sweep.io/', '_blank');
          closeMenu();
        }}
        startIcon={<HelpCenter size="medium" />}
      />

      <Divider />

      <UserMenuItem
        text="Notification center"
        callback={openNotificationsPopover}
        startIcon={
          <WithNotificationDot showDot={hasUnreadNotifications}>
            <NotificationIcon size="medium" />
          </WithNotificationDot>
        }
        endIcon={<ChevronRight size="medium" />}
        MenuItemPropsSx={isNotificationsPopoverOpen ? sharedHoverMenuItemStyles : {}}
      />

      {!multiAccounts && (
        <UserMenuItem
          text="Integrations"
          callback={() => {
            closeMenu();
            openIntegrationsDialog();
          }}
          startIcon={<IntegrationsIcon size="medium" />}
        />
      )}

      <UserMenuItem
        text="Log Out"
        MenuItemPropsSx={{
          color: colors.blush[600],
          '& svg path': {
            stroke: colors.blush[600],
          },
        }}
        callback={logout}
        startIcon={<SignOutIcon size="medium" />}
      />
    </>
  );
};

const UserMenuItem = ({
  callback,
  text,
  MenuItemPropsSx,
  startIcon,
  endIcon,
  user,
}: {
  callback?: () => void;
  text: string;
  MenuItemPropsSx?: SxProps;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  user?: UserInfo;
}) => {
  return (
    <MenuItem
      onClick={callback}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', ...MenuItemPropsSx }}
    >
      {user && (
        <AvatarPicture
          avatar={{ emoji: user.emoji, imageUrl: user.imageUrl }}
          clickable={false}
          dimension={28}
          isInactive={getIsUserInactive(user?.status)}
        />
      )}
      {startIcon}
      <Typography variant="body">{text}</Typography>
      <Box ml="auto" display="flex" alignItems="center">
        {endIcon}
      </Box>
    </MenuItem>
  );
};

export { MenuItemsUser };
